import React, { useState } from 'react';
import { Dropdown } from './design-system';
import { cn } from '../lib/utils';
import { ChevronDown, ChevronUp } from 'lucide-react';

// Moods hidden from the filter UI (still present on product data untouched).
const MOOD_HIDDEN = ['Elegante'];

// Componente para sección colapsable
const FilterSection = ({ title, children, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border-b border-brand-black/10 pb-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between py-2 text-label-md text-brand-black/70 hover:text-brand-black transition-colors"
      >
        <span>{title}</span>
        {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {isOpen && <div className="mt-2">{children}</div>}
    </div>
  );
};

// Componente para chips seleccionables (multi-select)
const ChipSelector = ({ options, selected, onChange, multiSelect = false }) => {
  const handleClick = (value) => {
    if (multiSelect) {
      const currentSelected = selected ? selected.split(',').filter(Boolean) : [];
      if (currentSelected.includes(value)) {
        onChange(currentSelected.filter(v => v !== value).join(','));
      } else {
        onChange([...currentSelected, value].join(','));
      }
    } else {
      onChange(selected === value ? '' : value);
    }
  };

  const isSelected = (value) => {
    if (multiSelect) {
      const currentSelected = selected ? selected.split(',').filter(Boolean) : [];
      return currentSelected.includes(value);
    }
    return selected === value;
  };

  return (
    <div className="flex flex-wrap gap-2">
      {options.map(({ value, label }) => (
        <button
          key={value}
          onClick={() => handleClick(value)}
          className={cn(
            "px-3 py-1.5 text-xs rounded-full transition-all",
            isSelected(value)
              ? "bg-brand-cobalt/10 border border-brand-cobalt text-brand-cobalt font-medium"
              : "bg-white border border-brand-black/20 text-brand-black/70 hover:border-brand-cobalt hover:text-brand-black"
          )}
        >
          {label}
        </button>
      ))}
    </div>
  );
};

const FilterPanel = ({ filters, selectedFilters, onFilterChange, availableFilters }) => {
  return (
    <div className="space-y-4 brand-card p-4 rounded-lg" data-testid="filter-panel">
      <div className="pb-2 border-b border-brand-black/10">
        <h3 className="text-heading-sm text-brand-black">Filtros</h3>
      </div>

      {/* Género */}
      <FilterSection title="GÉNERO" defaultOpen={true}>
        <ChipSelector
          options={[
            { value: 'Hombre', label: 'Hombre' },
            { value: 'Mujer', label: 'Mujer' },
            { value: 'Unisex', label: 'Unisex' }
          ]}
          selected={selectedFilters.gender}
          onChange={(value) => onFilterChange('gender', value)}
        />
      </FilterSection>

      {/* Tipo de Perfume */}
      <FilterSection title="TIPO DE PERFUME" defaultOpen={true}>
        <ChipSelector
          options={[
            { value: 'Diseñador', label: 'Diseñador' },
            { value: 'Nicho', label: 'Nicho' }
          ]}
          selected={selectedFilters.perfume_type}
          onChange={(value) => onFilterChange('perfume_type', value)}
        />
      </FilterSection>

      {/* Familia Olfativa (Multi-select) */}
      <FilterSection title="FAMILIA OLFATIVA" defaultOpen={true}>
        <ChipSelector
          options={
            (availableFilters?.olfactory_families || [
              'Cítrico', 'Fresco', 'Dulce', 'Madera', 'Floral'
            ]).map(f => ({ value: f, label: f }))
          }
          selected={selectedFilters.olfactory_family}
          onChange={(value) => onFilterChange('olfactory_family', value)}
          multiSelect={true}
        />
      </FilterSection>

      {/* Mood / Ocasión (Multi-select) */}
      <FilterSection title="OCASIÓN / MOOD" defaultOpen={false}>
        <ChipSelector
          options={
            (availableFilters?.moods || [
              'Perfecto para la noche',
              'Para el día a día',
              'Elegante',
              'Sexy'
            ])
              .filter(m => !MOOD_HIDDEN.includes(m))
              .map(m => ({ value: m, label: m }))
          }
          selected={selectedFilters.mood}
          onChange={(value) => onFilterChange('mood', value)}
          multiSelect={true}
        />
      </FilterSection>

      {/* Proyección */}
      <FilterSection title="PROYECCIÓN" defaultOpen={false}>
        <ChipSelector
          options={[
            { value: 'Sutil', label: 'Sutil' },
            { value: 'Intenso', label: 'Intenso' },
            { value: 'Monstruosa', label: 'Monstruosa' }
          ]}
          selected={selectedFilters.intensity}
          onChange={(value) => onFilterChange('intensity', value)}
        />
      </FilterSection>

      {/* Marca (Dropdown) */}
      <FilterSection title="MARCA" defaultOpen={false}>
        <Dropdown
          options={[
            { value: '', label: 'Todas las marcas' },
            ...(availableFilters?.brands || []).map(brand => ({
              value: brand,
              label: brand
            }))
          ]}
          value={selectedFilters.brand || ''}
          onChange={(value) => onFilterChange('brand', value)}
          placeholder="Selecciona una marca"
        />
      </FilterSection>

      {/* Clear Filters */}
      <button
        onClick={() => onFilterChange('clear', null)}
        className="w-full py-3 mt-4 text-label-md text-brand-black/60 hover:text-brand-cobalt border border-brand-black/20 hover:border-brand-cobalt transition-colors rounded"
        data-testid="clear-filters-btn"
      >
        Limpiar Filtros
      </button>
    </div>
  );
};

export default FilterPanel;
